// Component: StrategyTreeView.jsx
// Purpose: Thin Strategy Builder Tree container (filter, collapse/expand, section jumps) over the shared ConditionRailTree author mode.

import React from 'react';
import { getConnectedEdges } from 'reactflow';
import {
  FiArrowLeft,
  FiChevronsDown,
  FiChevronsUp,
  FiCrosshair,
  FiHash,
  FiInfo,
  FiPlus,
  FiSearch,
  FiTag,
  FiTrash2,
  FiX,
} from 'react-icons/fi';
import { Button, Input, Modal, Tooltip } from '../../../../../components/ds';
import ConditionRailTree, { ROW_ACTION_CLASS } from './ConditionRailTree';
import { CanvasViewSwitch } from './CanvasToolbar';
import SearchableListbox from './SearchableListbox';
import { ToolboxItemRow } from './StrategyToolbox';
import useTreeInteraction from '../hooks/useTreeInteraction';
import useTreeAuthoring from '../hooks/useTreeAuthoring';
import { CHART_VISUAL_TYPES } from '../config/nodeVisualConfig';
import {
  eligibleExistingOperandSources,
  eligiblePriceOperandSources,
  resolveSourceFields,
  resolveTargetHandle,
} from '../utils/operandLinking';
import { COMPARATOR_OPS, dataSourceTitle, resolveSmartLabel } from '../utils/nodeUtils';
import {
  groupItems,
  pickerItems,
  sectionItems,
  toolboxItems,
} from '../utils/toolboxCatalogue';

const TREE_LABEL_OPTIONS = [
  { id: 'names', label: 'Names', Icon: FiTag },
  { id: 'fields', label: 'Fields', Icon: FiHash },
];

const findTreeCatalogueSingleton = (nodes = [], type, data = {}) => {
  if (type === 'data_source' && !data?.isSecondary) {
    return nodes.find((node) => node.type === 'data_source' && !node.data?.isSecondary) || null;
  }
  if (CHART_VISUAL_TYPES.includes(type)) {
    return nodes.find((node) => node.type === type) || null;
  }
  if (type === 'chart_output' && data?.panel === 'main') {
    return nodes.find((node) => node.type === type && node.data?.panel === 'main') || null;
  }
  return null;
};

export const availableSectionItems = (section, filteredIndicators = [], nodes = []) => (
  sectionItems(section, filteredIndicators)
    .filter((item) => !findTreeCatalogueSingleton(nodes, item.type, item.data))
);

const rowText = (row) => [
  row.label, row.referenceText, row.flagText,
  ...(row.inputSlots || []).map(({ chip }) => chip?.label),
].filter(Boolean).join(' ').toLowerCase();

const filterRow = (row, query) => {
  if (!query) return row;
  if (rowText(row).includes(query)) return row;
  const children = (row.children || []).map((child) => filterRow(child, query)).filter(Boolean);
  return children.length > 0 ? { ...row, children } : null;
};

const flatten = (roots, collapsedKeys = null) => {
  const rows = [];
  const visit = (row) => {
    rows.push(row);
    if (!collapsedKeys?.has(row.key)) (row.children || []).forEach(visit);
  };
  roots.forEach(visit);
  return rows;
};

const filterSections = (sections, filter, collapsedKeys) => {
  const query = filter.trim().toLowerCase();
  return (sections || []).map((section) => {
    const roots = (section.roots || []).map((row) => filterRow(row, query)).filter(Boolean);
    return { ...section, roots, rows: flatten(roots), visibleRows: flatten(roots, collapsedKeys) };
  });
};

export function SharedByTriggersNotice({ nodeId, model }) {
  const triggerIds = model?.sharedById?.[nodeId] || [];
  if (triggerIds.length < 2) return null;
  const names = triggerIds.map((id) => {
    const row = model.sections?.flatMap(({ rows = [] }) => rows).find(({ nodeId: candidate }) => candidate === id);
    return row?.label || model.nodesById?.[id]?.data?.label || id;
  });
  return (
    <p className="text-[10px] text-fg-2">
      Shared by {triggerIds.length} triggers: {names.join(', ')}
    </p>
  );
}

export default function StrategyTreeView({
  model = { sections: [], nodesById: {}, sharedById: {}, edges: [] },
  selectedId,
  onSelect,
  onLocate,
  graphWiring, //! required: the parent's ONE useGraphWiring instance (built on the same role-locked nodes as model)
  onDeleteNode,
  createNode,
  updateNodeData,
  filteredIndicators = [],
  availableDataFields,
  allIndicatorMetadata,
  labelMode = 'names',
  onLabelModeChange,
  toolbar = null,
  compact = false,
  className = '',
}) {
  const [filter, setFilter] = React.useState('');
  const [filterFocused, setFilterFocused] = React.useState(false);
  const [compactFilterExpanded, setCompactFilterExpanded] = React.useState(false);
  const [collapsedKeys, setCollapsedKeys] = React.useState(() => new Set());
  const [status, setStatus] = React.useState('');
  const [ancestorTrail, setAncestorTrail] = React.useState([]);
  const [focusOccurrence, setFocusOccurrence] = React.useState(null);
  const [focusSlotRequest, setFocusSlotRequest] = React.useState(null);
  const [popup, setPopup] = React.useState(null);
  const [pendingDelete, setPendingDelete] = React.useState(null);
  const [deleteFocusRequest, setDeleteFocusRequest] = React.useState(null);
  const filterRef = React.useRef(null);
  const compactFilterButtonRef = React.useRef(null);
  const cancelDeleteRef = React.useRef(null);
  const deleteOpenerRef = React.useRef(null);
  const restoreDeleteOpenerRef = React.useRef(false);
  const shortcutFilterRef = React.useRef({ focus: () => {} });
  const sectionRefs = React.useRef({});
  const treeRootRef = React.useRef(null);
  const sections = React.useMemo(
    () => filterSections(model.sections, filter, collapsedKeys),
    [collapsedKeys, filter, model.sections],
  );
  const graphNodes = React.useMemo(
    () => Object.values(model.nodesById || {}),
    [model.nodesById],
  );
  const sectionCatalogueItems = React.useCallback(
    (sectionId) => availableSectionItems(sectionId, filteredIndicators, graphNodes),
    [filteredIndicators, graphNodes],
  );
  const parentName = React.useMemo(() => {
    const parent = ancestorTrail[0];
    if (!parent) return '';
    return resolveSmartLabel(model.nodesById?.[parent.nodeId], graphNodes, model.edges || []);
  }, [ancestorTrail, graphNodes, model.edges, model.nodesById]);
  const focusFilter = React.useCallback(() => {
    if (compact && !compactFilterExpanded) {
      setCompactFilterExpanded(true);
      return;
    }
    filterRef.current?.focus();
  }, [compact, compactFilterExpanded]);
  shortcutFilterRef.current.focus = focusFilter;
  const selectRow = React.useCallback((node) => {
    setAncestorTrail([]);
    onSelect?.(node);
  }, [onSelect]);
  const catalogueItems = React.useMemo(() => {
    const candidates = [
      ...toolboxItems(filteredIndicators),
      ...groupItems(),
      ...pickerItems(filteredIndicators).flatMap(({ items = [] }) => items),
    ];
    return candidates.filter((item, index) => (
      candidates.findIndex((candidate) => candidate.id === item.id) === index
    ));
  }, [filteredIndicators]);
  const treeAuthoring = useTreeAuthoring({
    nodes: Object.values(model.nodesById || {}),
    edges: model.edges || [],
    graphWiring,
    createNode,
    updateNodeData,
    setSelectedNode: onSelect,
    catalogueItems,
    availableDataFields,
    allIndicatorMetadata,
    stagingActive: popup?.kind === 'value' && popup.stage === 'outputs',
  });
  const createAndAttach = React.useCallback((request) => {
    const outcome = treeAuthoring.createAndAttach(request);
    setStatus(outcome.ok || outcome.duplicate ? '' : (outcome.reason || 'That item could not be added.'));
    return outcome;
  }, [treeAuthoring]);
  const interaction = useTreeInteraction({
    sections,
    nodesById: model.nodesById,
    filterRef: shortcutFilterRef,
    setCollapsedKeys,
    selectedId,
    onSelect: selectRow,
    onLocate,
    edges: model.edges,
    graphWiring,
    onDeleteNode,
    onCreateAndAttach: createAndAttach,
    setStatus,
  });
  const pendingDeleteEdges = React.useMemo(() => {
    if (!pendingDelete) return [];
    const pendingNode = model.nodesById?.[pendingDelete.row.nodeId] || pendingDelete.row.node;
    return pendingNode ? getConnectedEdges([pendingNode], model.edges || []) : [];
  }, [model.edges, model.nodesById, pendingDelete]);

  React.useEffect(() => {
    if (pendingDelete) {
      cancelDeleteRef.current?.focus();
    } else if (restoreDeleteOpenerRef.current) {
      restoreDeleteOpenerRef.current = false;
      deleteOpenerRef.current?.focus();
    }
  }, [pendingDelete]);

  const dismissDelete = React.useCallback(() => {
    restoreDeleteOpenerRef.current = true;
    setPendingDelete(null);
  }, []);

  const openAddMenu = React.useCallback((event, target, items, label) => {
    event.preventDefault();
    event.stopPropagation();
    setPopup({
      kind: 'add',
      opener: event.currentTarget,
      target,
      label,
      items,
      stage: 'items',
    });
  }, []);

  const openConditionMenu = React.useCallback((event, target) => {
    openAddMenu(event, target, groupItems(), 'Condition');
  }, [openAddMenu]);

  const openOperatorMenu = React.useCallback((event, node) => {
    event.preventDefault();
    event.stopPropagation();
    setPopup({ kind: 'operator', opener: event.currentTarget, node, stage: 'operators' });
  }, []);

  const openOperandPicker = React.useCallback((intent) => {
    const targetNode = model.nodesById?.[intent?.target];
    if (!targetNode) {
      setStatus('The operand target is unavailable.');
      return;
    }
    const inputs = targetNode.data?.inputs || [];
    const targetHandle = resolveTargetHandle(targetNode, intent.targetHandle);
    const inputIndex = Math.max(0, inputs.findIndex(({ id }) => id === targetHandle));
    setPopup({
      kind: 'value',
      opener: document.activeElement,
      openerSlotKey: intent.slotKey || null,
      targetNode,
      targetHandle,
      inputIndex,
      stage: 'values',
    });
  }, [model.nodesById]);

  const valueGroups = React.useMemo(() => {
    if (popup?.kind !== 'value') return [];
    if (popup.stage === 'outputs') {
      return [{
        id: 'outputs',
        label: 'Choose output',
        options: popup.pendingOutputs.map((field) => ({
          id: `output:${popup.pendingNode.id}:${field}`,
          label: field,
          choice: {
            kind: 'existing-output',
            sourceNode: popup.pendingNode,
            field,
            createdEdges: popup.createdEdges,
          },
        })),
      }];
    }
    const graphNodes = Object.values(model.nodesById || {});
    const priceOptions = eligiblePriceOperandSources(graphNodes, popup.targetNode)
      .flatMap((source) => resolveSourceFields(source, availableDataFields, allIndicatorMetadata).map((field) => ({
        id: `price:${source.id}:${field}`,
        label: `${dataSourceTitle(source)} · ${field}`,
        choice: { kind: 'price-field', sourceNode: source, field },
      })));
    const existingOptions = eligibleExistingOperandSources(graphNodes, popup.targetNode)
      .flatMap((node) => resolveSourceFields(node, availableDataFields, allIndicatorMetadata).map((field) => ({
        id: `existing:${node.id}:${field}`,
        label: `${resolveSmartLabel(node, graphNodes, model.edges || [])} · ${field}`,
        choice: { kind: 'existing-output', sourceNode: node, field },
      })));
    const pickerCatalogue = pickerItems(filteredIndicators);
    const newItems = pickerCatalogue
      .filter(({ id }) => id === 'new-indicator' || id === 'math')
      .flatMap(({ items = [] }) => items);
    const newOptions = newItems.map((item) => ({
      id: `new:${item.id}`,
      label: item.name || item.label,
      item,
      choice: { kind: 'new-indicator', item },
    }));
    return [
      { id: 'price', label: 'Price', options: priceOptions },
      { id: 'existing', label: 'In this strategy', options: existingOptions },
      { id: 'new', label: 'New indicator', options: newOptions },
      {
        id: 'constant',
        label: 'Constant',
        options: [{
          id: 'constant',
          label: 'Use typed number as constant',
          matchesQuery: (rawQuery) => rawQuery.trim() === '' || Number.isFinite(Number(rawQuery)),
          choice: { kind: 'constant' },
        }],
      },
    ];
  }, [allIndicatorMetadata, availableDataFields, filteredIndicators, model.edges, model.nodesById, popup]);

  const handlePopupSelect = React.useCallback((option, { query }) => {
    if (popup.kind === 'add') {
      createAndAttach({ item: option.item, target: popup.target });
      return undefined;
    }
    if (popup.kind === 'operator') {
      const outcome = treeAuthoring.setOperator({ nodeId: popup.node.id, op: option.value });
      setStatus(outcome.ok ? '' : outcome.reason);
      return undefined;
    }
    let choice = option.choice;
    if (choice.kind === 'constant') {
      const value = Number(query);
      if (query.trim() === '' || !Number.isFinite(value)) {
        setStatus('Type a number, then choose Constant.');
        return false;
      }
      choice = { ...choice, value };
    }
    const outcome = treeAuthoring.pickOperand({
      targetNode: popup.targetNode,
      inputIndex: popup.inputIndex,
      targetHandle: popup.targetHandle,
      choice,
    });
    if (outcome.pendingOutputs) {
      setPopup((current) => ({
        ...current,
        stage: 'outputs',
        pendingNode: outcome.node,
        pendingOutputs: outcome.pendingOutputs,
        createdEdges: outcome.createdEdges,
      }));
      setStatus('Choose an indicator output.');
      return false;
    }
    setStatus(outcome.ok ? '' : outcome.reason);
    return outcome.ok ? undefined : false;
  }, [createAndAttach, popup, treeAuthoring]);

  const handlePopupClose = React.useCallback((reason) => {
    if (popup?.openerSlotKey) {
      setFocusSlotRequest({
        key: popup.openerSlotKey,
        opener: popup.opener,
        waitForReplacement: reason === 'select',
      });
    }
    setPopup(null);
  }, [popup]);

  const popupGroups = React.useMemo(() => {
    if (!popup) return [];
    if (popup.kind === 'add') {
      return [{
        id: 'add',
        label: popup.label,
        options: popup.items.map((item) => ({
          id: `add:${item.id}`,
          label: item.name || item.label,
          item,
        })),
      }];
    }
    if (popup.kind === 'operator') {
      return [{
        id: 'operators',
        label: 'Operator',
        options: treeAuthoring.operatorOptions.map((option) => ({ ...option, label: `${option.glyph} ${option.label}` })),
      }];
    }
    return valueGroups;
  }, [popup, treeAuthoring.operatorOptions, valueGroups]);

  const selectOccurrence = React.useCallback((nodeId, trail = []) => {
    const node = model.nodesById?.[nodeId];
    if (!node) return;
    setAncestorTrail(trail);
    onSelect?.(node);
  }, [model.nodesById, onSelect]);

  const goBack = React.useCallback(() => {
    const [next, ...remaining] = ancestorTrail;
    if (!next) return;
    setAncestorTrail(remaining);
    setFocusOccurrence(next);
    const node = model.nodesById?.[next.nodeId];
    if (node) onSelect?.(node);
  }, [ancestorTrail, model.nodesById, onSelect]);

  React.useLayoutEffect(() => {
    if (!focusOccurrence || !treeRootRef.current) return;
    const row = [...treeRootRef.current.querySelectorAll('[data-tree-row-key]')]
      .find((element) => element.dataset.treeRowKey === focusOccurrence.rowKey);
    const segment = row ? [...row.querySelectorAll('[data-node-id]')]
      .find((element) => element.dataset.nodeId === focusOccurrence.nodeId && typeof element.focus === 'function') : null;
    (segment || row)?.focus?.();
    setFocusOccurrence(null);
  }, [focusOccurrence, sections]);

  React.useLayoutEffect(() => {
    if (!deleteFocusRequest || pendingDelete || !treeRootRef.current) return;
    const rows = [...treeRootRef.current.querySelectorAll('[data-tree-row-key]')];
    const original = rows.find(({ dataset }) => dataset.treeRowKey === deleteFocusRequest.rowKey);
    const next = rows.find(({ dataset }) => dataset.treeRowKey === deleteFocusRequest.nextRowKey);
    (original || next)?.focus?.();
    setDeleteFocusRequest(null);
  }, [deleteFocusRequest, pendingDelete, sections]);

  React.useLayoutEffect(() => {
    if (!focusSlotRequest || !treeRootRef.current || popup) return;
    const candidate = [...treeRootRef.current.querySelectorAll('[data-operand-slot-key]')]
      .find((element) => (
        element.dataset.operandSlotKey === focusSlotRequest.key
        && element.matches('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      ));
    if (!candidate || (focusSlotRequest.waitForReplacement && candidate === focusSlotRequest.opener)) return;
    candidate.focus();
    setFocusSlotRequest(null);
  }, [focusSlotRequest, popup, sections]);

  const collapseAll = () => setCollapsedKeys(new Set(
    sections.flatMap(({ rows = [] }) => rows.filter(({ children = [] }) => children.length > 0).map(({ key }) => key)),
  ));
  const expandAll = () => setCollapsedKeys(new Set());

  React.useLayoutEffect(() => {
    if (compact && compactFilterExpanded) filterRef.current?.focus();
  }, [compact, compactFilterExpanded]);

  const filterExpanded = compactFilterExpanded || Boolean(filter);
  const filterInput = (
    <div className={`relative min-w-0 ${compact ? 'flex-1' : 'w-full max-w-[360px] min-w-[160px] justify-self-center'}`}>
      {!compact ? (
        <FiSearch
          size={13}
          aria-hidden="true"
          className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-fg-3"
        />
      ) : null}
      <Input
        ref={filterRef}
        type="search"
        size="sm"
        aria-label="Filter strategy tree"
        value={filter}
        onChange={(event) => setFilter(event.target.value)}
        onFocus={() => setFilterFocused(true)}
        onBlur={(event) => {
          setFilterFocused(false);
          if (compact && !filter && event.relatedTarget !== compactFilterButtonRef.current) {
            setCompactFilterExpanded(false);
          }
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Escape') return;
          if (filter) {
            event.preventDefault();
            event.stopPropagation();
            setFilter('');
            return;
          }
          event.currentTarget.blur();
        }}
        className={`${compact ? 'pl-2' : 'pl-7'} pr-7 [&::-webkit-search-cancel-button]:appearance-none`}
        placeholder="Filter conditions…"
      />
      {filter ? (
        <button
          type="button"
          aria-label="Clear filter"
          onClick={() => {
            setFilter('');
            filterRef.current?.focus();
          }}
          className={`${ROW_ACTION_CLASS} absolute right-1.5 top-1/2 -translate-y-1/2`}
        >
          <FiX size={12} aria-hidden="true" />
        </button>
      ) : !filterFocused ? (
        <kbd
          aria-hidden="true"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded border border-divider px-1 font-mono text-[10px] text-fg-3"
        >
          /
        </kbd>
      ) : null}
    </div>
  );
  const filterControl = compact ? (
    <div className="flex min-w-0 flex-1 items-center gap-1">
      <Button
        ref={compactFilterButtonRef}
        variant="ghost"
        size="sm"
        iconOnly
        aria-label="Filter conditions"
        aria-expanded={filterExpanded}
        onClick={focusFilter}
      >
        <FiSearch size={13} aria-hidden="true" />
      </Button>
      {filterExpanded ? filterInput : null}
    </div>
  ) : filterInput;

  return (
    <div className={`flex min-h-0 flex-col gap-0 ${className}`}>
      <div data-testid="tree-header">
        <div
          role="toolbar"
          aria-label="Tree tools"
          className={compact
            ? 'flex h-9 items-center gap-1'
            : 'grid h-9 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3'}
        >
          <div className="flex items-center gap-2 [&_[role=radiogroup]]:h-7 [&_[role=radiogroup]]:p-0 [&_[role=radio]]:h-6 [&_[role=radio]]:py-0 [&_[role=radio]:focus-visible]:outline-none [&_[role=radio]:focus-visible]:ring-1 [&_[role=radio]:focus-visible]:ring-primary">
            {!compact ? toolbar : null}
            {!compact && toolbar ? <span aria-hidden="true" className="h-4 w-px bg-divider" /> : null}
            <CanvasViewSwitch
              ariaLabel="Tree labels"
              subView={labelMode}
              onSubViewChange={onLabelModeChange}
              options={TREE_LABEL_OPTIONS}
            />
          </div>
          {filterControl}
          <div className="flex items-center justify-end gap-1">
            {ancestorTrail.length > 0 ? (
              <Tooltip content={`Back to ${parentName}`}>
                <Button variant="ghost" size="sm" aria-label="Back to parent occurrence" onClick={goBack}>
                  <FiArrowLeft size={13} aria-hidden="true" />
                  Back
                </Button>
              </Tooltip>
            ) : null}
            <Tooltip content="Collapse all">
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label="Collapse all"
                onClick={collapseAll}
              >
                <FiChevronsUp size={14} aria-hidden="true" />
              </Button>
            </Tooltip>
            <Tooltip content="Expand all">
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label="Expand all"
                onClick={expandAll}
              >
                <FiChevronsDown size={14} aria-hidden="true" />
              </Button>
            </Tooltip>
          </div>
        </div>
        <nav
          aria-label="Strategy tree sections"
          className={`mt-1.5 flex items-center gap-1 overflow-x-auto border-b border-divider pb-2 [scrollbar-width:none] ${compact ? 'snap-x' : ''}`}
        >
          {sections.map((section) => (
            <button
              type="button"
              key={section.id}
              title={`Jump to ${section.label}`}
              onClick={() => sectionRefs.current[section.id]?.scrollIntoView?.({ block: 'start', behavior: 'smooth' })}
              className={`inline-flex h-6 shrink-0 items-center gap-1 rounded-full border border-divider bg-bg-2 px-2.5 text-[11px] font-medium text-fg-2 hover:bg-bg-3 hover:text-fg-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary ${compact ? 'snap-start' : ''} ${section.roots.length === 0 ? 'opacity-60' : ''}`}
            >
              {section.label}
              <span aria-hidden="true" className="tabular-nums text-[10px] text-fg-3">{section.roots.length}</span>
            </button>
          ))}
        </nav>
      </div>
      {popup ? (
        <SearchableListbox
          key={`${popup.kind}:${popup.stage}`}
          ariaLabel={popup.kind === 'add' ? 'Add to strategy tree' : (popup.kind === 'operator' ? 'Choose operator' : 'Choose operand')}
          groups={popupGroups}
          openerRef={popup.opener}
          onSelect={handlePopupSelect}
          onClose={handlePopupClose}
          renderOption={(option) => (
            option.item
              ? <ToolboxItemRow item={option.item} compact showDescription={false} />
              : <div className="px-2 py-1.5">{option.label}</div>
          )}
        />
      ) : null}
      {/* one labelled role="tree" per section, so the section heading sits outside the tree (B12); the delegated
          keyboard/drag handlers stay on this wrapper, so arrow keys still move across sections */}
      <div ref={treeRootRef} role="group" aria-label="Strategy tree" className="min-h-0 overflow-x-auto overflow-y-auto pt-1" {...interaction.rootProps}>
        {sections.map((section) => (
          <section key={section.id} aria-labelledby={`strategy-tree-${section.id}`}>
            <div
              data-tree-authoring-section={section.id}
              className="sticky top-0 z-20 mt-3 flex items-center bg-bg-1 py-1 text-[11px] font-bold uppercase tracking-[.07em] text-fg-3"
            >
              <h3
                id={`strategy-tree-${section.id}`}
                ref={(element) => { sectionRefs.current[section.id] = element; }}
              >
                {section.label}
                {/* the count is decoration: aria-hidden keeps the heading (and the tree it labels) named by the section */}
                <span aria-hidden="true" className="ml-1.5 font-medium normal-case">{section.roots.length}</span>
              </h3>
              {sectionCatalogueItems(section.id).length > 0 ? (
                <button
                  type="button"
                  aria-label={`Add to ${section.label}`}
                  title={`Add to ${section.label}`}
                  onClick={(event) => openAddMenu(
                    event,
                    { sectionId: section.id },
                    sectionCatalogueItems(section.id),
                    section.label,
                  )}
                  className={`${ROW_ACTION_CLASS} ml-1 align-middle`}
                >
                  <FiPlus size={13} aria-hidden="true" />
                </button>
              ) : null}
            </div>
            <div role="tree" aria-labelledby={`strategy-tree-${section.id}`}>
              <ConditionRailTree
                authorMode
                authorRows={section.roots}
                collapsedKeys={collapsedKeys}
                openDetailKey={interaction.openDetailKey}
                onToggleRow={interaction.toggle}
                selectedId={selectedId}
                sameNodeId={interaction.sameNodeId}
                activeKey={interaction.activeKey}
                labelMode={labelMode}
                onSelectNode={selectOccurrence}
                onPickOperand={openOperandPicker}
                onAddCondition={openConditionMenu}
                renderRowAction={(row, {
                  hasDetail, detailOpen, detailId, canAddCondition,
                }) => (
                  <>
                    {/* details toggle (also `i`); rows without detail content get none */}
                    {hasDetail ? (
                      <button
                        type="button"
                        data-tree-details={row.key}
                        aria-label="Show details"
                        title="Show details"
                        aria-expanded={detailOpen}
                        aria-controls={detailId}
                        className={ROW_ACTION_CLASS}
                      >
                        <FiInfo size={13} aria-hidden="true" />
                      </button>
                    ) : null}
                    {row.node?.type === 'comparator' ? (
                      <button
                        type="button"
                        aria-label={`Change operator for ${row.label}`}
                        title="Change comparison operator"
                        onClick={(event) => openOperatorMenu(event, row.node)}
                        className={`${ROW_ACTION_CLASS} w-auto px-1 font-mono text-[10px]`}
                      >
                        {COMPARATOR_OPS[row.node.data?.operator]?.glyph || row.node.data?.operator || '?'}
                      </button>
                    ) : null}
                    {canAddCondition ? (
                      <button
                        type="button"
                        aria-label={`Add condition to ${row.label}`}
                        title="Add condition"
                        onClick={(event) => openConditionMenu(event, row.conditionAttachTarget)}
                        className={ROW_ACTION_CLASS}
                      >
                        <FiPlus size={13} aria-hidden="true" />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      data-tree-locate={row.key}
                      aria-label="Show on canvas"
                      title="Show on canvas (L)"
                      className={ROW_ACTION_CLASS}
                    >
                      <FiCrosshair size={13} aria-hidden="true" />
                    </button>
                    {row.deletable !== false ? (
                      <button
                        type="button"
                        aria-label={`Delete ${row.label}`}
                        title={`Delete ${row.label}`}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          const rowElement = event.currentTarget.closest('[data-tree-row-key]');
                          const visibleRows = treeRootRef.current
                            ? [...treeRootRef.current.querySelectorAll('[role="treeitem"]')]
                            : [];
                          const rowIndex = visibleRows.indexOf(rowElement);
                          const nextRow = visibleRows[rowIndex + 1] || visibleRows[rowIndex - 1] || null;
                          deleteOpenerRef.current = event.currentTarget;
                          restoreDeleteOpenerRef.current = false;
                          setPendingDelete({
                            row,
                            rowKey: rowElement?.dataset.treeRowKey || row.key,
                            nextRowKey: nextRow?.dataset.treeRowKey || null,
                          });
                        }}
                        className={ROW_ACTION_CLASS}
                      >
                        <FiTrash2 size={13} aria-hidden="true" />
                      </button>
                    ) : null}
                  </>
                )}
              />
            </div>
          </section>
        ))}
      </div>
      <div role="status" aria-live="polite" className="min-h-[1rem] text-[10px] text-fg-2">{status}</div>
      <Modal
        open={Boolean(pendingDelete)}
        onClose={dismissDelete}
        size="sm"
        title={pendingDelete ? `Delete ${pendingDelete.row.label}?` : null}
        footer={pendingDelete ? (
          <>
            <Button ref={cancelDeleteRef} variant="ghost" size="sm" onClick={dismissDelete}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                restoreDeleteOpenerRef.current = false;
                const request = {
                  rowKey: pendingDelete.rowKey,
                  nextRowKey: pendingDelete.nextRowKey,
                };
                interaction.deleteRow(pendingDelete.row);
                setPendingDelete(null);
                setDeleteFocusRequest(request);
              }}
            >
              Delete
            </Button>
          </>
        ) : null}
      >
        {pendingDelete ? (
          <p className="ds-body">
            This deletes the node and {pendingDeleteEdges.length} connected {pendingDeleteEdges.length === 1 ? 'link' : 'links'}.
            {pendingDelete.row.kind === 'group' ? ' Its children stay but become unattached.' : ''}
          </p>
        ) : null}
      </Modal>
    </div>
  );
}
